import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ToastProvider } from '../components';
import { browserLanguage, I18nProvider, useI18n } from '../lib/i18n';
import { QueryClientProvider } from '../lib/query';
import { BrowserRouter, ScrollToTop, useRoutes } from '../lib/router';
import { setTheme, getTheme } from '../lib/branding';
import { AppShell } from './AppShell';
import { AuthGate } from './AuthGate';
import { useMe } from './me';
import { PageErrorBoundary } from './PageErrorBoundary';
import { ROUTES } from './routes';
import { ServicesProvider, useServices, type Services } from './services';
import { SyncRuntime } from './SyncRuntime';
import { PublicRouteSwitch } from '../features/public-report/PublicRouteSwitch';

const LANG_KEY = 'sss.lang';

/** Interface language: the user's profile language once signed in, else the device's. */
function LanguageRoot({ children }: { children: ReactNode }) {
  const { api, auth } = useServices();
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || browserLanguage());
  const change = useCallback(
    (l: string, opts: { remote?: boolean } = {}) => {
      setLang(l);
      try {
        localStorage.setItem(LANG_KEY, l);
      } catch {
        /* ignore */
      }
      if (opts.remote !== false && auth.current()) void api.patch('/me', { language: l }).catch(() => {});
    },
    [api, auth],
  );
  return (
    <I18nProvider lang={lang} onChange={change}>
      {children}
    </I18nProvider>
  );
}

/** Adopt the language saved on the user's profile after sign-in. */
function ProfileLanguage() {
  const { me } = useMe();
  const { lang, setLanguage } = useI18n();
  useEffect(() => {
    if (me.user.language && me.user.language !== lang) setLanguage(me.user.language, { remote: false });
  }, [me.user.language]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** The routed page inside the shell. */
export function AppRoutes() {
  return useRoutes(ROUTES);
}

export function App({ services }: { services: Services }) {
  useEffect(() => setTheme(getTheme()), []);
  return (
    <ServicesProvider services={services}>
      <QueryClientProvider client={services.queryClient}>
        <LanguageRoot>
          <ToastProvider>
            <BrowserRouter>
              <ScrollToTop />
              {/* Public pages (the customer's secure report link) skip sign-in. */}
              <PublicRouteSwitch>
                <AuthGate>
                  {({ versionStatus, versionPolicy }) => (
                    <>
                      <ProfileLanguage />
                      <SyncRuntime />
                      <AppShell versionStatus={versionStatus} versionPolicy={versionPolicy}>
                        <PageErrorBoundary>
                          <AppRoutes />
                        </PageErrorBoundary>
                      </AppShell>
                    </>
                  )}
                </AuthGate>
              </PublicRouteSwitch>
            </BrowserRouter>
          </ToastProvider>
        </LanguageRoot>
      </QueryClientProvider>
    </ServicesProvider>
  );
}
